import { FileText, Users, Brain, ExternalLink, BookOpen, Presentation as Citation, Award, Globe, Signal as Journal } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Hero } from '../../components/Hero';
import useSWR from 'swr';

interface Author {
  author: {
    display_name: string;
  };
}

interface Work {
  id: string;
  title: string;
  authorships: Author[];
  publication_date: string;
  host_venue: {
    display_name: string;
  };
  cited_by_count: number;
  doi: string;
  type: string;
  abstract: string;
}

interface OpenAlexResponse {
  results: Work[];
  meta: {
    count: number;
  };
}

const fetcher = async (url: string) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch publications');
  }
  return response.json();
};

const SEARCH_URL = 'https://api.openalex.org/works?page=1&filter=authorships.author.id:a5113546177&sort=publication_year:desc&per_page=100';

export function Research() {
  const { data, error, isLoading } = useSWR<OpenAlexResponse>(SEARCH_URL, fetcher);

  const publications = data?.results || [];
  const totalPublications = data?.meta.count || 0;
  const totalCitations = publications.reduce((sum, pub) => sum + (pub.cited_by_count || 0), 0);

  // Group publications by year
  const publicationsByYear = publications.reduce((acc, pub) => {
    const year = new Date(pub.publication_date).getFullYear();
    if (!acc[year]) acc[year] = [];
    acc[year].push(pub);
    return acc;
  }, {} as Record<number, Work[]>);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Hero
        title="Research Publications"
        description="Exploring the frontiers of neurological science through groundbreaking research and clinical studies."
        image="https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&q=80"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Research Impact Stats */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-2xl p-12 mb-16">
          <div className="text-center mb-8">
            <Award className="h-16 w-16 mx-auto mb-4" />
            <h2 className="text-4xl font-bold">Research Impact</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">{totalPublications}</div>
              <div className="text-xl opacity-90">Publications</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">{totalCitations}</div>
              <div className="text-xl opacity-90">Citations</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">40+</div>
              <div className="text-xl opacity-90">Years Experience</div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12 text-red-600">
            An error occurred while loading publications. Please try again later.
          </div>
        )}

        {/* Publications by Year */}
        {Object.entries(publicationsByYear)
          .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
          .map(([year, works]) => (
            <div key={year} className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
                <Journal className="h-8 w-8 text-blue-600 mr-3" />
                {year} Publications
              </h2>
              <div className="space-y-8">
                {works.map((work) => (
                  <div 
                    key={work.id}
                    className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all border border-gray-100"
                  >
                    <h3 className="text-2xl font-semibold text-gray-900 mb-4 hover:text-blue-600">
                      {work.title}
                    </h3>
                    <div className="flex flex-wrap gap-6 text-sm text-gray-600 mb-6">
                      <span className="flex items-center">
                        <Users className="h-5 w-5 mr-2 text-blue-600" />
                        {work.authorships.map(a => a.author.display_name).join(', ')}
                      </span>
                      <span className="flex items-center">
                        <Journal className="h-5 w-5 mr-2 text-blue-600" />
                        {work.host_venue?.display_name}
                      </span>
                      <span className="flex items-center">
                        <Citation className="h-5 w-5 mr-2 text-blue-600" />
                        {work.cited_by_count} citations
                      </span>
                    </div>
                    {work.abstract && (
                      <div className="bg-gray-50 rounded-lg p-6 mb-6">
                        <h4 className="font-semibold text-gray-900 mb-2">Abstract</h4>
                        <p className="text-gray-600">{work.abstract}</p>
                      </div>
                    )}
                    {work.doi && (
                      <a
                        href={`https://doi.org/${work.doi}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                      >
                        <ExternalLink className="h-5 w-5 mr-2" />
                        View Full Publication
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

        {/* Research Collaboration CTA */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-12 text-center">
          <Globe className="h-16 w-16 text-blue-600 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Research Collaboration</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Interested in collaborating on neurological research? We welcome opportunities
            to work with other researchers and institutions.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-blue-700 transition-colors text-lg"
          >
            <Users className="h-6 w-6 mr-2" />
            Discuss Research Opportunities
          </Link>
        </div>
      </div>
    </div>
  );
}